/**
 * Settings Folder — app-owned local directory for Plugins, Filters, and Scripts.
 *
 * Independent from the Disk plugin's Fonts folder. Persisted under its own
 * IndexedDB key and configured only from Preferences (and Plugin Manager).
 */
import { NativeAdapter, SETTINGS_FOLDER_HANDLE_KEY } from './file-system-adapter';
import { ensureSettingsFolderSubdirectories, SETTINGS_FOLDER_PATHS } from './settings-folder-paths';
import { Logger } from './logger';
const console = new Logger('SettingsFolder');
/** Source id used in script-editor URIs for Settings Folder backed files. */
export const SETTINGS_FOLDER_SOURCE_ID = 'settings';
class SettingsFolderService {
    constructor() {
        this.initializePromise = null;
        /** Cached directory handles for picker startIn (must be sync at gesture time). */
        this.startInHandles = new Map();
        this.openPickerInFlight = false;
        this.savePickerInFlight = false;
        this.adapter = new NativeAdapter(SETTINGS_FOLDER_HANDLE_KEY);
    }
    getAdapter() {
        return this.adapter;
    }
    getFolderName() {
        if (!this.adapter.hasDirectory()) {
            return null;
        }
        return this.adapter.getDirectoryName() || null;
    }
    hasFolder() {
        return this.adapter.hasDirectory();
    }
    async getAccessState() {
        await this.initialize();
        if (!this.adapter.hasDirectory()) {
            return 'missing';
        }
        const permission = await this.adapter.checkPermission();
        return permission === 'granted' ? 'ready' : 'needsRenewal';
    }
    async renewPermission() {
        if (!this.adapter.hasDirectory()) {
            return false;
        }
        const permission = await this.adapter.requestPermission();
        if (permission !== 'granted') {
            return false;
        }
        await ensureSettingsFolderSubdirectories(this.adapter);
        await this.refreshStartInCache();
        this.dispatchAccessChanged({
            hasSettingsFolderAccess: true,
            source: 'renew'
        });
        return true;
    }
    async isReady() {
        await this.initialize();
        return this.adapter.hasDirectory();
    }
    async initialize() {
        if (this.adapter.hasDirectory()) {
            if (this.startInHandles.size === 0) {
                await this.refreshStartInCache();
            }
            return true;
        }
        if (!this.initializePromise) {
            this.initializePromise = this.adapter
                .initialize()
                .then(async (restored) => {
                if (restored || this.adapter.hasDirectory()) {
                    await this.refreshStartInCache();
                }
                return this.adapter.hasDirectory();
            })
                .finally(() => {
                this.initializePromise = null;
            });
        }
        return this.initializePromise;
    }
    /**
     * Prompt the user to choose a Settings Folder, create managed subfolders,
     * and notify dependents.
     */
    async selectFolder(options) {
        const selected = await this.adapter.selectDirectory(options);
        if (!selected) {
            return false;
        }
        await ensureSettingsFolderSubdirectories(this.adapter);
        await this.refreshStartInCache();
        this.dispatchAccessChanged({
            hasSettingsFolderAccess: true,
            source: 'attach'
        });
        console.log('Settings Folder selected:', this.adapter.getDirectoryName());
        return true;
    }
    async clearFolder() {
        await this.adapter.clearDirectory();
        this.startInHandles.clear();
        this.dispatchAccessChanged({
            hasSettingsFolderAccess: false,
            source: 'detach'
        });
        console.log('Settings Folder cleared');
    }
    async ensureSubdirectories() {
        if (!this.adapter.hasDirectory()) {
            throw new Error('Choose a Settings Folder first.');
        }
        await ensureSettingsFolderSubdirectories(this.adapter);
        await this.refreshStartInCache();
    }
    /**
     * Resolve a cached startIn handle without awaiting filesystem I/O.
     * Awaits before show*FilePicker consume user activation and cause
     * NotAllowedError even when a picker still appears from a concurrent call.
     */
    getCachedStartInHandle(path) {
        if (path && this.startInHandles.has(path)) {
            return this.startInHandles.get(path);
        }
        if (path === '/' || !path) {
            return this.startInHandles.get('/');
        }
        return this.startInHandles.get('/') ?? undefined;
    }
    async refreshStartInCache() {
        this.startInHandles.clear();
        const root = this.adapter.getDirectoryHandle();
        if (!root) {
            return;
        }
        this.startInHandles.set('/', root);
        for (const path of Object.values(SETTINGS_FOLDER_PATHS)) {
            try {
                const handle = await this.adapter.getHandleAtPath(path);
                if (handle && handle.kind === 'directory') {
                    this.startInHandles.set(path, handle);
                }
            }
            catch {
                // Subfolder may not exist yet.
            }
        }
    }
    showOpenFilePicker(options) {
        if (!this.adapter.hasDirectory()) {
            return Promise.resolve(null);
        }
        if (this.openPickerInFlight) {
            return Promise.resolve(null);
        }
        const pickerOptions = {
            multiple: false
        };
        if (options?.types) {
            pickerOptions.types = options.types;
        }
        const startHandle = this.getCachedStartInHandle(options?.startIn || '/');
        if (startHandle) {
            pickerOptions.startIn = startHandle;
        }
        this.openPickerInFlight = true;
        let nativePromise;
        try {
            // Invoke synchronously so the call stays inside the user gesture.
            nativePromise = window.showOpenFilePicker(pickerOptions);
        }
        catch (error) {
            this.openPickerInFlight = false;
            if (error instanceof DOMException && error.name === 'AbortError') {
                return Promise.resolve(null);
            }
            console.error('Error opening Settings Folder file picker:', error);
            return Promise.reject(error);
        }
        return nativePromise
            .then(async ([fileHandle]) => this.getRelativePath(fileHandle))
            .catch((error) => {
            if (error instanceof DOMException &&
                error.name === 'AbortError') {
                console.log('Open file picker cancelled');
                return null;
            }
            console.error('Error opening Settings Folder file picker:', error);
            throw error;
        })
            .finally(() => {
            this.openPickerInFlight = false;
        });
    }
    showSaveFilePicker(options) {
        if (!this.adapter.hasDirectory()) {
            return Promise.resolve(null);
        }
        if (this.savePickerInFlight) {
            return Promise.resolve(null);
        }
        const pickerOptions = {};
        if (options?.suggestedName) {
            pickerOptions.suggestedName = options.suggestedName;
        }
        if (options?.types) {
            pickerOptions.types = options.types;
        }
        const startHandle = this.getCachedStartInHandle(options?.startIn || '/');
        if (startHandle) {
            pickerOptions.startIn = startHandle;
        }
        this.savePickerInFlight = true;
        let nativePromise;
        try {
            // Invoke synchronously so the call stays inside the user gesture.
            nativePromise = window.showSaveFilePicker(pickerOptions);
        }
        catch (error) {
            this.savePickerInFlight = false;
            if (error instanceof DOMException && error.name === 'AbortError') {
                return Promise.resolve(null);
            }
            console.error('Error saving Settings Folder file picker:', error);
            return Promise.reject(error);
        }
        return nativePromise
            .then(async (fileHandle) => this.getRelativePath(fileHandle))
            .catch((error) => {
            if (error instanceof DOMException &&
                error.name === 'AbortError') {
                console.log('Save file picker cancelled');
                return null;
            }
            console.error('Error saving Settings Folder file picker:', error);
            throw error;
        })
            .finally(() => {
            this.savePickerInFlight = false;
        });
    }
    async getRelativePath(fileHandle) {
        try {
            const rootHandle = this.adapter.getDirectoryHandle();
            if (!rootHandle) {
                return null;
            }
            const pathParts = await rootHandle.resolve(fileHandle);
            if (pathParts === null) {
                console.warn('Selected file is outside the Settings Folder root');
                return null;
            }
            return '/' + pathParts.join('/');
        }
        catch (error) {
            console.error('Error resolving Settings Folder path:', error);
            return null;
        }
    }
    dispatchAccessChanged(detail) {
        window.dispatchEvent(new CustomEvent('settingsFolderAccessChanged', { detail }));
    }
}
export const settingsFolder = new SettingsFolderService();
export { SETTINGS_FOLDER_PATHS };
